import { Bell, Search, User, Moon, Sun, Menu, LogOut, ChevronDown, AlertTriangle, X, ExternalLink, Image, CheckCircle, Info, Warehouse, Maximize, Minimize, Inbox, CheckSquare } from 'lucide-react';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { LanguageSwitcher } from '../LanguageSwitcher';
import { SessionTimeout } from './SessionTimeout';
import { api, type Notification } from '@/api/client';
import { useAuthStore } from '@/store/useAuthStore';
import { useThemeStore } from '@/store/useThemeStore';
import { useFullscreenToggle } from '@/hooks/useFullscreenToggle';
import { formatCustom } from '../../utils/dateUtils';

interface GpsrWarning {
  id: string;
  name: string;
  missingFields: string[];
  source: 'supplier' | 'manufacturer' | 'brand';
}

// Event for notifying components about BISPix completion
export const BISPIX_COMPLETE_EVENT = 'bispix-complete';

interface HeaderProps {
  onToggleSidebar: () => void;
}

/**
 * V-2382: Glocken mit `data.i18n` ({ titel, text, werte }) werden in der Sprache des Benutzers angezeigt, der Server-Text
 * (deutsch) bleibt Vorgabe. Alle anderen Glocken bleiben unverändert (Pendant-Umstellung: Folgekarte).
 */
type GlockenUebersetzung = { titel?: string; text?: string; werte?: Record<string, string> };
const glockenI18n = (n: Notification): GlockenUebersetzung | null => {
  const i18n = (n.data as { i18n?: GlockenUebersetzung } | undefined | null)?.i18n;
  return i18n && typeof i18n === 'object' ? i18n : null;
};

export function Header({ onToggleSidebar }: HeaderProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { resolvedTheme, toggleTheme } = useThemeStore();
  const { isFullscreen, toggle: toggleFullscreen } = useFullscreenToggle();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [gpsrWarnings, setGpsrWarnings] = useState<GpsrWarning[]>([]);
  const [gpsrBackfilling, setGpsrBackfilling] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);
  const { user, clear: clearAuth, activeWarehouse, setActiveWarehouse } = useAuthStore();
  const [warehouseMenuOpen, setWarehouseMenuOpen] = useState(false);
  const [warehouses, setWarehouses] = useState<{ id: string; name: string; code: string; isDefault: number }[]>([]);
  const warehouseMenuRef = useRef<HTMLDivElement>(null);
  const [creditBalance, setCreditBalance] = useState<number | null>(null);

  // Offene Kundenanfragen im Postfach. Ohne diese Zahl merkt niemand, dass eine Anfrage wartet —
  // genau so lag auf Chera eine Käuferfrage tagelang unbeantwortet. Fehlschläge werden bewusst
  // verschluckt: der Header steht auf JEDER Seite, er darf an einem Zähler nicht scheitern.
  const [unansweredMessages, setUnansweredMessages] = useState(0);
  useEffect(() => {
    let cancelled = false;

    const load = () => {
      api
        .get<{ unanswered?: number }>('/ebay/messages/unread-count')
        .then((r) => {
          if (!cancelled) setUnansweredMessages(r?.unanswered ?? 0);
        })
        .catch(() => undefined);
    };

    load();
    // Der Poller holt alle 5 Minuten neu — die Anzeige zieht in demselben Takt nach.
    const timer = setInterval(load, 5 * 60 * 1000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, []);

  // Guthaben-Saldo (Cent) fuer die Anzeige unter dem Benutzernamen laden.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = (await api.get('/user-billing/credits/balance')) as { success?: boolean; balance?: number };
        if (!cancelled && res?.success && typeof res.balance === 'number') {
          setCreditBalance(res.balance);
        }
      } catch {
        /* Guthaben optional – bei Fehler einfach nicht anzeigen */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Load warehouses and active warehouse on mount
  useEffect(() => {
    const loadWarehouses = async () => {
      try {
        const [whRes, activeRes] = await Promise.all([
          api.get('/auth/warehouses') as Promise<any>,
          api.get('/auth/active-warehouse') as Promise<any>,
        ]);
        if (whRes?.success && whRes.warehouses) {
          setWarehouses(whRes.warehouses);
        }
        if (activeRes?.success && activeRes.warehouse) {
          setActiveWarehouse(activeRes.warehouse);
        }
      } catch {
        // Ignore errors
      }
    };
    loadWarehouses();
  }, []);

  const handleWarehouseChange = async (warehouseId: string | null) => {
    try {
      const res = await api.post('/auth/active-warehouse', { warehouseId }) as any;
      if (res?.success) {
        setActiveWarehouse(res.warehouse);
      }
    } catch {
      // Ignore errors
    }
    setWarehouseMenuOpen(false);
  };

  // Close menus when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target as Node)) {
        setNotificationsOpen(false);
      }
      if (warehouseMenuRef.current && !warehouseMenuRef.current.contains(event.target as Node)) {
        setWarehouseMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load notifications
  const loadNotifications = useCallback(async (isInitial = false) => {
    try {
      if (isInitial) setLoadingNotifications(true);
      const [supplierGpsr, brandGpsr, notifResponse] = await Promise.all([
        api.getGpsrIncompleteSuppliers().catch(() => ({ suppliers: [], count: 0 })),
        api.getGpsrIncompleteBrands().catch(() => ({ brands: [], count: 0 })),
        api.getNotifications(),
      ]);
      setGpsrWarnings([
        ...(supplierGpsr.suppliers || []).map(s => ({
          id: s.id,
          name: s.name,
          missingFields: s.missingFields,
          source: 'supplier' as const,
        })),
        ...(brandGpsr.brands || []).map(b => ({
          id: b.id,
          name: b.name,
          missingFields: b.missingFields,
          source: 'brand' as const,
        })),
      ]);
      setNotifications(notifResponse.notifications);

      // Dispatch event for BISPix notifications if user is on article images page
      const bisPixNotifs = notifResponse.notifications.filter(n => n.type === 'bispix_complete' && !n.isRead);
      if (bisPixNotifs.length > 0) {
        bisPixNotifs.forEach(n => {
          const data = n.data as { articleId?: string; imageId?: string; processedUrl?: string } | undefined;
          if (data?.articleId && location.pathname.includes(`/articles/${data.articleId}`)) {
            window.dispatchEvent(new CustomEvent(BISPIX_COMPLETE_EVENT, { detail: data }));
          }
        });
      }
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      if (isInitial) setLoadingNotifications(false);
    }
  }, [location.pathname]);

  useEffect(() => {
    loadNotifications(true);
    const interval = setInterval(() => loadNotifications(false), 60000);
    return () => clearInterval(interval);
  }, [loadNotifications]);

  const handleGpsrBackfill = async () => {
    setGpsrBackfilling(true);
    try {
      await api.backfillGpsrSuppliers();
      await loadNotifications(false);
    } catch (err) {
      console.error('GPSR backfill failed:', err);
    } finally {
      setGpsrBackfilling(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    try {
      await api.markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await api.markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  const handleDeleteNotification = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Failed to delete notification:', err);
    }
  };

  const unreadNotifications = notifications.filter(n => !n.isRead);
  const totalNotifications = gpsrWarnings.length + unreadNotifications.length;

  const getNotificationIcon = (type: string, icon: string) => {
    if (type === 'bispix_complete') return <Image size={16} className="text-green-600" />;
    if (icon === 'success') return <CheckCircle size={16} className="text-green-600" />;
    if (icon === 'warning') return <AlertTriangle size={16} className="text-amber-600" />;
    return <Info size={16} className="text-blue-600" />;
  };

  const handleLogout = async () => {
    await api.logout();
    clearAuth();
    navigate('/login');
  };

  const getRoleLabel = (role: string) => {
    const labels: Record<string, string> = {
      admin: 'Administrator',
      manager: 'Manager',
      warehouse: 'Lager',
      shipping: 'Versand',
      readonly: 'Nur Lesen',
    };
    return labels[role] || role;
  };

  return (
    <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6">
      <div className="flex items-center gap-3 flex-1">
        {/* Mobile menu toggle - only visible on small screens */}
        <button
          onClick={onToggleSidebar}
          className="p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors touch-manipulation lg:hidden"
          aria-label="Menu"
        >
          <Menu size={22} />
        </button>

        <div className="relative max-w-md flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
          <input
            type="text"
            placeholder={`${t('common.search')}...`}
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation"
          />
        </div>
      </div>

      <div className="flex items-center gap-2 md:gap-4">
        {/* Session Timeout Indicator */}
        <SessionTimeout />

        {/* Language Switcher */}
        <LanguageSwitcher />

        {/* Warehouse Selector - only visible if >1 warehouse */}
        {warehouses.length > 1 && (
          <div className="relative hidden md:block" ref={warehouseMenuRef}>
            <button
              onClick={() => setWarehouseMenuOpen(!warehouseMenuOpen)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 text-sm text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <Warehouse size={16} />
              <span className="max-w-[120px] truncate">{activeWarehouse?.name || t('warehouse.allWarehouses')}</span>
              <ChevronDown size={14} />
            </button>
            {warehouseMenuOpen && (
              <div className="absolute right-0 mt-1 w-56 bg-white rounded-lg shadow-lg border border-slate-200 z-50 py-1">
                <button
                  onClick={() => handleWarehouseChange(null)}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${!activeWarehouse ? 'text-blue-600 font-medium' : 'text-slate-700'}`}
                >
                  {t('warehouse.allWarehouses')}
                </button>
                {warehouses.map((wh) => (
                  <button
                    key={wh.id}
                    onClick={() => handleWarehouseChange(wh.id)}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${activeWarehouse?.id === wh.id.toLowerCase() ? 'text-blue-600 font-medium' : 'text-slate-700'}`}
                  >
                    {wh.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <button
          onClick={toggleTheme}
          className="p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors touch-manipulation"
          title={resolvedTheme === 'dark' ? t('common.lightMode') : t('common.darkMode')}
        >
          {resolvedTheme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
        </button>

        <button
          onClick={toggleFullscreen}
          className="p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors touch-manipulation hidden sm:inline-flex"
          title={isFullscreen ? 'Vollbild beenden' : 'Vollbild'}
        >
          {isFullscreen ? <Minimize size={22} /> : <Maximize size={22} />}
        </button>

        {/* Postfach — zeigt die Zahl der unbeantworteten Kundenanfragen als Badge */}
        <button
          onClick={() => navigate('/postfach')}
          className="p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors relative touch-manipulation"
          title={t('nav.mailbox')}
          aria-label={t('nav.mailbox')}
        >
          <Inbox size={22} />
          {unansweredMessages > 0 && (
            <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-amber-500 rounded-full text-white text-xs flex items-center justify-center px-1">
              {unansweredMessages > 9 ? '9+' : unansweredMessages}
            </span>
          )}
        </button>

        {/* Aufgaben */}
        <button
          onClick={() => navigate('/tasks')}
          className="p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors touch-manipulation"
          title={t('nav.tasks')}
          aria-label={t('nav.tasks')}
        >
          <CheckSquare size={22} />
        </button>

        {/* Notifications */}
        <div className="relative" ref={notificationsRef}>
          <button
            onClick={() => setNotificationsOpen(!notificationsOpen)}
            className="p-2.5 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors relative touch-manipulation"
          >
            <Bell size={22} />
            {totalNotifications > 0 && (
              <span className="absolute top-1 right-1 min-w-[18px] h-[18px] bg-red-500 rounded-full text-white text-xs flex items-center justify-center px-1">
                {totalNotifications > 9 ? '9+' : totalNotifications}
              </span>
            )}
          </button>

          {/* Notifications Dropdown */}
          {notificationsOpen && (
            <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-lg border border-slate-200 z-50 max-h-[400px] overflow-hidden">
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-semibold text-slate-800">Benachrichtigungen</h3>
                <button
                  onClick={() => setNotificationsOpen(false)}
                  className="p-1 text-slate-400 hover:text-slate-600 rounded"
                >
                  <X size={18} />
                </button>
              </div>

              <div className="overflow-y-auto max-h-[320px]">
                {loadingNotifications ? (
                  <div className="px-4 py-6 text-center text-slate-500">Laden...</div>
                ) : totalNotifications === 0 ? (
                  <div className="px-4 py-6 text-center text-slate-500">
                    Keine Benachrichtigungen
                  </div>
                ) : (
                  <>
                    {/* General Notifications (BISPix, etc.) */}
                    {unreadNotifications.length > 0 && (
                      <>
                        <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 flex items-center justify-between">
                          <span className="text-blue-700 text-sm font-medium">
                            Neue Benachrichtigungen ({unreadNotifications.length})
                          </span>
                          <button
                            onClick={handleMarkAllRead}
                            className="text-xs text-blue-600 hover:text-blue-800"
                          >
                            Alle gelesen
                          </button>
                        </div>
                        {unreadNotifications.slice(0, 5).map((notif) => (
                          <div
                            key={notif.id}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-100 transition-colors group"
                          >
                            <Link
                              to={notif.link || '#'}
                              onClick={() => {
                                handleMarkAsRead(notif.id);
                                setNotificationsOpen(false);
                              }}
                              className="flex items-start gap-3 flex-1 min-w-0"
                            >
                              <div className={`p-2 rounded-lg ${notif.type === 'bispix_complete' ? 'bg-green-100' : 'bg-blue-100'}`}>
                                {getNotificationIcon(notif.type, notif.icon)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-slate-800 truncate">
                                  {glockenI18n(notif)?.titel
                                    ? t(glockenI18n(notif)!.titel!, { ...(glockenI18n(notif)!.werte ?? {}), defaultValue: notif.title })
                                    : notif.title}
                                </p>
                                {notif.message && (
                                  <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">
                                    {glockenI18n(notif)?.text
                                      ? t(glockenI18n(notif)!.text!, { ...(glockenI18n(notif)!.werte ?? {}), defaultValue: notif.message })
                                      : notif.message}
                                  </p>
                                )}
                                <p className="text-xs text-slate-400 mt-1">
                                  {formatCustom(notif.createdAt, { hour: '2-digit', minute: '2-digit' }, 'time')}
                                </p>
                              </div>
                            </Link>
                            <button
                              onClick={(e) => handleDeleteNotification(notif.id, e)}
                              className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded opacity-0 group-hover:opacity-100 transition-all"
                              title="Löschen"
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}
                      </>
                    )}

                    {/* GPSR Warnings Section */}
                    {gpsrWarnings.length > 0 && (
                      <>
                        <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
                          <div className="flex items-center gap-2 text-amber-700 text-sm font-medium">
                            <AlertTriangle size={16} />
                            {t('dashboard.quickAccess.gpsrTitle')} ({gpsrWarnings.length})
                          </div>
                          <button
                            onClick={handleGpsrBackfill}
                            disabled={gpsrBackfilling}
                            className="mt-1 text-xs text-amber-700 hover:text-amber-900 underline disabled:opacity-50 disabled:no-underline"
                          >
                            {gpsrBackfilling
                              ? t('dashboard.quickAccess.gpsrBackfilling')
                              : t('dashboard.quickAccess.gpsrBackfill')}
                          </button>
                        </div>
                        {gpsrWarnings.slice(0, 3).map((warning) => (
                          <Link
                            key={`${warning.source}-${warning.id}`}
                            to={warning.source === 'supplier' ? `/purchasing/suppliers?edit=${warning.id}` : warning.source === 'brand' ? `/settings/brands?edit=${warning.id}` : `/settings/manufacturers?edit=${warning.id}`}
                            onClick={() => setNotificationsOpen(false)}
                            className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 border-b border-slate-100 transition-colors"
                          >
                            <div className="p-2 bg-amber-100 rounded-lg">
                              <AlertTriangle size={16} className="text-amber-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-slate-800 truncate">{warning.name}</p>
                              <p className="text-xs text-slate-500 mt-0.5">
                                {t('dashboard.quickAccess.missing')}: {warning.missingFields.join(', ')}
                              </p>
                            </div>
                            <ExternalLink size={14} className="text-slate-400 flex-shrink-0 mt-1" />
                          </Link>
                        ))}
                        {gpsrWarnings.length > 3 && (
                          <Link
                            to="/dashboard"
                            onClick={() => setNotificationsOpen(false)}
                            className="block px-4 py-3 text-center text-sm text-blue-600 hover:bg-blue-50 transition-colors"
                          >
                            {t('dashboard.quickAccess.showAllGpsrWarnings', { count: gpsrWarnings.length })}
                          </Link>
                        )}
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>

        {/* User menu with dropdown */}
        <div className="relative" ref={userMenuRef}>
          <button
            onClick={() => setUserMenuOpen(!userMenuOpen)}
            className="flex items-center gap-2 pl-4 border-l border-slate-200 hover:bg-slate-50 rounded-lg py-1 pr-2 transition-colors touch-manipulation"
          >
            <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center">
              <User size={18} className="text-white" />
            </div>
            <div className="hidden md:block text-sm text-left">
              <p className="font-medium text-slate-700">{user?.name || 'Benutzer'}</p>
              {creditBalance !== null && (
                <p className="text-emerald-600 text-xs font-medium">
                  {(creditBalance / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' })} Guthaben
                </p>
              )}
              <p className="text-slate-400 text-xs">{user?.role ? getRoleLabel(user.role) : ''}</p>
            </div>
            <ChevronDown size={16} className="hidden md:block text-slate-400" />
          </button>

          {/* Dropdown menu */}
          {userMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-1 z-50">
              <button
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/profile');
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors"
              >
                <User size={18} />
                {t('nav.profile', 'Profil')}
              </button>
              <div className="border-t border-slate-200 my-1" />
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <LogOut size={18} />
                {t('auth.logout')}
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
